/**
 * 變更密碼頁面
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle2, ShieldCheck } from 'lucide-react';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, updatePassword } = useAuth();
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const togglePassword = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // 驗證
    if (formData.newPassword !== formData.confirmPassword) {
      setError('新密碼與確認密碼不一致');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('密碼長度至少需要 6 個字元');
      return;
    }

    if (formData.newPassword === formData.currentPassword) {
      setError('新密碼不能與目前密碼相同');
      return;
    }

    setLoading(true);

    try {
      const { error } = await updatePassword(formData.newPassword);

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setError('目前密碼不正確');
        } else {
          setError('變更密碼失敗，請稍後再試');
        }
        setLoading(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/member');
      }, 2000);
    } catch (err) {
      setError('變更密碼失敗，請稍後再試');
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 py-8">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* 返回按鈕 */}
        <Button
          variant="ghost"
          onClick={() => navigate('/member')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          返回會員中心
        </Button>

        <Card className="bg-white/80 backdrop-blur-xl border-white/20 shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">變更密碼</CardTitle>
                <CardDescription>
                  定期更新密碼以保護您的帳號安全
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {success ? (
              <div className="text-center py-8 space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">密碼已更新！</h3>
                <p className="text-gray-600">即將返回會員中心...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <Alert variant="destructive" className="animate-shake">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* 安全提示 */}
                <Alert className="border-blue-200 bg-blue-50">
                  <ShieldCheck className="h-5 w-5 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <strong>密碼安全建議：</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                      <li>至少 6 個字元</li>
                      <li>包含大小寫字母和數字</li>
                      <li>避免使用個人資訊</li>
                    </ul>
                  </AlertDescription>
                </Alert>

                {/* 目前密碼 */}
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">目前密碼</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="currentPassword"
                      name="currentPassword"
                      type={showPasswords.current ? 'text' : 'password'}
                      placeholder="輸入目前密碼"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      required
                      className="pl-10 pr-10 h-12 bg-white/50"
                    />
                    <button
                      type="button"
                      onClick={() => togglePassword('current')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.current ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* 新密碼 */}
                <div className="space-y-2">
                  <Label htmlFor="newPassword">新密碼</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type={showPasswords.new ? 'text' : 'password'}
                      placeholder="至少 6 個字元"
                      value={formData.newPassword}
                      onChange={handleChange}
                      required
                      minLength={6}
                      className="pl-10 pr-10 h-12 bg-white/50"
                    />
                    <button
                      type="button"
                      onClick={() => togglePassword('new')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.new ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* 確認新密碼 */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">確認新密碼</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      placeholder="再次輸入新密碼"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className="pl-10 pr-10 h-12 bg-white/50"
                    />
                    <button
                      type="button"
                      onClick={() => togglePassword('confirm')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* 提交按鈕 */}
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/member')}
                    className="flex-1"
                  >
                    取消
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        更新中...
                      </div>
                    ) : (
                      '更新密碼'
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}

export default ChangePasswordPage;
